import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import PrintableDocument from '@/components/PrintableDocument';
import RFIForm from '@/components/RFIForm';
import { api } from '@/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  FileQuestion, 
  Send, 
  MessageSquare, 
  Clock, 
  Calendar,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  FileText,
  Paperclip,
  Download,
  Wand2,
  ArrowRight,
  RefreshCw,
  Loader2,
  User,
  Users,
  Building2,
  Mail,
  Trash2,
  Edit3,
  Upload,
  X,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Bell,
  Brain
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { isProfessionalRole } from '@/utils/roles';
import usePermissions from '@/hooks/usePermissions';
import CorrespondenceTab from '@/components/correspondence/CorrespondenceTab';
import SendToTaskGenerator from '@/components/correspondence/SendToTaskGenerator';
import DocumentGeneratorButton from '@/components/DocumentGeneratorButton';

const STATUS_COLORS = {
  Draft: 'bg-slate-100 text-slate-700 border-slate-200',
  Issued: 'bg-blue-100 text-blue-700 border-blue-200',
  'Awaiting Response': 'bg-amber-100 text-amber-700 border-amber-200',
  Answered: 'bg-blue-100 text-blue-700 border-blue-200',
  Closed: 'bg-green-100 text-green-700 border-green-200',
  Overdue: 'bg-red-100 text-red-700 border-red-200'
};

const PRIORITY_COLORS = {
  Low: 'bg-slate-50 text-slate-600 border-slate-200',
  Medium: 'bg-blue-50 text-blue-600 border-blue-200',
  High: 'bg-orange-50 text-orange-600 border-orange-200',
  Critical: 'bg-red-50 text-red-700 border-red-200'
};

export default function RFIDetail() {
  const { projectId, rfiId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rfi, setRfi] = useState(null);
  const [project, setProject] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [issuing, setIssuing] = useState(false);
  const [generatingNarrative, setGeneratingNarrative] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  
  const isAdmin = user?.role === 'admin';
  const permissions = usePermissions();
  
  // Dialogs
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closureEffects, setClosureEffects] = useState([]);
  const [responseAttachments, setResponseAttachments] = useState([]);
  const [uploadingResponse, setUploadingResponse] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [expandedLogEntry, setExpandedLogEntry] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analyzingCorrespondence, setAnalyzingCorrespondence] = useState(false);
  
  // Issue form
  const [issueForm, setIssueForm] = useState({
    send_type: 'external',
    send_email: true
  });
  
  // Response form
  const [responseForm, setResponseForm] = useState({
    response_text: '',
    respondent_name: '',
    respondent_email: ''
  });
  
  // Auto-populate response form for consultants/external users
  useEffect(() => {
    if (user && isProfessionalRole(user.role)) {
      setResponseForm(prev => ({
        ...prev,
        respondent_name: user.name || user.full_name || '',
        respondent_email: user.email || ''
      }));
    }
  }, [user]);
  
  // Closure form
  const [closureForm, setClosureForm] = useState({
    closure_effect: '',
    closure_notes: '',
    convert_to_delay_event: false,
    closed_by: '',
    closure_date: new Date().toISOString().split('T')[0],
    closure_attachments: []
  });
  const [closureUploading, setClosureUploading] = useState(false);
  const closureFileInputRef = useRef(null);

  useEffect(() => {
    if (projectId && rfiId) {
      loadRFI();
      loadClosureEffects();
    }
  }, [projectId, rfiId]);

  const loadRFI = async () => {
    if (!projectId || !rfiId) {
      toast.error('Invalid RFI reference');
      navigate(-1);
      return;
    }
    
    try {
      const rfiRes = await api.rfi.getById(projectId, rfiId);
      const rfiData = rfiRes.data;
      // Normalize field names — older IRS-created RFIs use subject/description instead of title/query
      if (!rfiData.title && rfiData.subject) rfiData.title = rfiData.subject;
      if (!rfiData.query && rfiData.description) rfiData.query = rfiData.description;
      if (!rfiData.date_created && rfiData.created_at) rfiData.date_created = rfiData.created_at;
      if (!rfiData.created_by_name && rfiData.created_by) rfiData.created_by_name = rfiData.created_by;
      setRfi(rfiData);
      
      // Load timeline separately so it doesn't block RFI loading
      try {
        const timelineRes = await api.rfi.getTimeline(projectId, rfiId);
        setTimeline(timelineRes.data.timeline || []);
      } catch (e) {
        console.error('Failed to load timeline:', e);
        setTimeline([]);
      }
      
      // Load project info for print header
      try {
        const projectRes = await api.projects.getById(projectId);
        setProject(projectRes.data.project || projectRes.data);
      } catch (e) {
        console.error('Failed to load project:', e);
      }
    } catch (error) {
      toast.error('Failed to load RFI');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeCorrespondence = async () => {
    setAnalyzingCorrespondence(true);
    setAiAnalysis(null);
    try {
      const res = await api.correspondence.analyzeModuleCorrespondence('rfi', rfiId);
      setAiAnalysis(res.data);
      toast.success('AI analysis complete');
    } catch (error) {
      toast.error('AI analysis failed');
    } finally {
      setAnalyzingCorrespondence(false);
    }
  };

  const handleDownloadEml = async (correspondenceId) => {
    try {
      const res = await api.correspondence.downloadCorrespondenceEml(correspondenceId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'email.eml');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download email');
    }
  };

  const handleDownloadAttachment = async (correspondenceId, attachmentId, filename) => {
    try {
      const res = await api.correspondence.downloadCorrespondenceAttachment(correspondenceId, attachmentId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'attachment');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download attachment');
    }
  };

  const loadClosureEffects = async () => {
    try {
      const response = await api.rfi.getClosureEffects();
      setClosureEffects(response.data);
    } catch (error) {
      console.error('Failed to load closure effects');
    }
  };

  const handleIssue = async () => {
    if (!rfi.recipients || rfi.recipients.length === 0) {
      toast.error('Please add recipients before issuing');
      return;
    }
    
    setIssuing(true);
    try {
      const response = await api.rfi.issue(projectId, rfiId, {
        send_type: issueForm.send_type,
        send_email: issueForm.send_email
      });
      
      toast.success('RFI issued successfully');
      setIssueDialogOpen(false);
      
      // If email wasn't sent, open mailto link as fallback
      if (response.data.mailto_link && !response.data.email_sent) {
        const openMailto = window.confirm('Email could not be sent automatically. Open email client to send manually?');
        if (openMailto) {
          window.location.href = response.data.mailto_link;
        }
      }
      
      // Show PDF URL if available
      if (response.data.pdf_url) {
        toast.info('PDF generated and attached to email');
      }
      
      loadRFI();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue RFI');
    } finally {
      setIssuing(false);
    }
  };

  const openIssueDialog = () => {
    if (!rfi.recipients || rfi.recipients.length === 0) {
      toast.error('Please add recipients before issuing');
      return;
    }
    setIssueDialogOpen(true);
  };

  const handleRecordResponse = async () => {
    // For authenticated consultants, use their profile details
    const isConsultant = isProfessionalRole(user?.role);
    
    const finalResponseData = isConsultant ? {
      response_text: responseForm.response_text,
      respondent_name: user.name || user.full_name || responseForm.respondent_name,
      respondent_email: user.email || responseForm.respondent_email
    } : responseForm;
    
    if (!finalResponseData.response_text || !finalResponseData.respondent_name || !finalResponseData.respondent_email) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      setUploadingResponse(true);
      
      // First upload any attachments
      let attachmentUrls = [];
      if (responseAttachments.length > 0) {
        const formData = new FormData();
        responseAttachments.forEach((file) => {
          formData.append('files', file);
        });
        
        try {
          const uploadRes = await api.upload.multiple(formData);
          attachmentUrls = uploadRes.data.files || [];
        } catch (uploadErr) {
          console.error('Failed to upload attachments:', uploadErr);
          toast.error('Failed to upload attachments');
          return;
        }
      }
      
      // Record response with attachments
      await api.rfi.respond(projectId, rfiId, {
        ...finalResponseData,
        attachments: attachmentUrls
      });
      
      toast.success('Response recorded');
      setResponseDialogOpen(false);
      setResponseForm({ response_text: '', respondent_name: '', respondent_email: '' });
      setResponseAttachments([]);
      loadRFI();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record response');
    } finally {
      setUploadingResponse(false);
    }
  };

  const handleResponseFileChange = (e) => {
    const files = Array.from(e.target.files);
    // Accept .msg, .eml, .pdf, .doc, .docx, images
    const validExtensions = ['.msg', '.eml', '.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.gif'];
    const validFiles = files.filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      return validExtensions.includes(ext);
    });
    
    if (validFiles.length !== files.length) {
      toast.warning('Some files were not accepted. Allowed: .msg, .eml, .pdf, .doc, .docx, images');
    }
    
    setResponseAttachments(prev => [...prev, ...validFiles]);
  };

  const handleDroppedFiles = (files) => {
    // Accept .msg, .eml, .pdf, .doc, .docx, images
    const validExtensions = ['.msg', '.eml', '.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.gif', '.xlsx', '.xls'];
    const validFiles = files.filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      return validExtensions.includes(ext);
    });
    
    if (validFiles.length === 0) {
      toast.error('No valid files found. Allowed: .msg, .eml, .pdf, .doc, .docx, images');
      return;
    }
    
    if (validFiles.length !== files.length) {
      toast.warning('Some files were not accepted. Allowed: .msg, .eml, .pdf, .doc, .docx, images');
    }
    
    setResponseAttachments(prev => [...prev, ...validFiles]);
    toast.success(`${validFiles.length} file(s) added`);
  };

  const removeResponseAttachment = (index) => {
    setResponseAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Closure attachment handlers
  const handleClosureAttachmentUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setClosureUploading(true);
    const uploadedFiles = [];
    
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploadedFiles.push({
          filename: file.name,
          url: response.data.url,
          size: file.size,
          content_type: file.type
        });
      }
      
      setClosureForm(prev => ({
        ...prev,
        closure_attachments: [...prev.closure_attachments, ...uploadedFiles]
      }));
      toast.success(`${uploadedFiles.length} file(s) uploaded`);
    } catch (error) {
      toast.error('Failed to upload files');
    } finally {
      setClosureUploading(false);
    }
  };

  const removeClosureAttachment = (index) => {
    setClosureForm(prev => ({
      ...prev,
      closure_attachments: prev.closure_attachments.filter((_, i) => i !== index)
    }));
  };

  const handleClose = async () => {
    if (!closureForm.closure_effect) {
      toast.error('Please select a closure effect');
      return;
    }
    
    if (!closureForm.closed_by) {
      toast.error('Please enter who is closing this RFI');
      return;
    }
    
    if (!closureForm.closure_date) {
      toast.error('Please select a closure date');
      return;
    }
    
    try {
      const response = await api.rfi.close(projectId, rfiId, closureForm);
      toast.success('RFI closed');
      
      if (response.data.delay_event_id) {
        toast.success('Delay Event created from RFI');
      }
      
      setCloseDialogOpen(false);
      loadRFI();
    } catch (error) {
      toast.error('Failed to close RFI');
    }
  };

  const handleGenerateNarrative = async () => {
    setGeneratingNarrative(true);
    try {
      await api.rfi.generateNarrative(projectId, rfiId);
      toast.success('Narrative generated');
      loadRFI();
    } catch (error) {
      toast.error('Failed to generate narrative');
    } finally {
      setGeneratingNarrative(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.rfi.delete(projectId, rfiId);
      toast.success('RFI deleted successfully');
      navigate(`/projects/${projectId}#rfi`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete RFI';
      toast.error(message);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const [sendingReminder, setSendingReminder] = useState(false);
  const handleSendReminder = async () => {
    setSendingReminder(true);
    try {
      const res = await api.post(`/projects/${projectId}/rfis/${rfiId}/send-reminder`);
      const data = res.data;
      if (data.emails_sent_to?.length > 0) {
        toast.success(`Reminder sent to ${data.emails_sent_to.length} recipient(s)`);
      } else {
        toast.success('Reminder logged (no recipient emails configured)');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send reminder';
      toast.error(message);
    } finally {
      setSendingReminder(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center p-8">
          <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  if (!rfi) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center">
          <p className="text-slate-500">RFI not found</p>
        </div>
      </DashboardLayout>
    );
  }

  // Permission-based action controls (case-insensitive status checks for legacy data)
  const rfiStatus = (rfi.status || '').toLowerCase();
  // canIssue: Users with issue OR submit permission AND status allows issuing
  const canIssue = (rfiStatus === 'draft' || rfiStatus === 'issued' || rfiStatus === 'open') && (permissions.canIssueRFI() || permissions.can('rfis', 'submit') || permissions.can('rfis', 'edit'));
  // canRespond: Only users with respond permission AND status allows responding
  const canRespond = (rfiStatus === 'awaiting response' || rfiStatus === 'overdue') && permissions.canRespondRFI();
  // canEdit: Only users with edit permission AND RFI is not closed
  const canEdit = rfiStatus !== 'closed' && permissions.canEditRFI();
  // canClose: Only users with close permission AND status allows closing
  const canClose = rfiStatus !== 'closed' && rfiStatus !== 'draft' && permissions.canCloseRFI();

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6 overflow-x-hidden" data-testid="rfi-detail">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <button
            onClick={() => navigate(`/projects/${projectId}#rfi`)}
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-3 sm:mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Project
          </button>
          
          {/* Title row */}
          <div className="mb-3">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <h1 className="text-xl sm:text-2xl xl:text-3xl font-barlow font-bold uppercase text-slate-900">
                {rfi.rfi_number || rfi.title || 'RFI'}
              </h1>
              <Badge className={`${STATUS_COLORS[rfi.status]} text-xs`}>{rfi.status}</Badge>
              <Badge className={`${PRIORITY_COLORS[rfi.priority]} text-xs`}>{rfi.priority}</Badge>
            </div>
            {rfi.rfi_number && <p className="text-sm sm:text-base text-slate-600 line-clamp-2">{rfi.title}</p>}
            {!rfi.rfi_number && (rfiStatus === 'draft' || rfiStatus === 'open') && (
              <p className="text-xs text-amber-600 mt-1">
                RFI number will be assigned when issued
              </p>
            )}
            {rfi.source_type && (rfi.source_id || rfi.linked_irs_id) && (
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-slate-500 uppercase">Created from:</span>
                <button
                  onClick={() => {
                    if (rfi.source_type === 'Site Instruction') {
                      navigate(`/projects/${projectId}/si/${rfi.source_id}`);
                    } else if (rfi.source_type === 'Shop Drawing') {
                      navigate(`/projects/${projectId}/drawings/${rfi.source_id}`);
                    } else if (rfi.source_type === 'IRS' || rfi.source_type === 'irs') {
                      navigate(`/projects/${projectId}#irs`);
                    }
                  }}
                  className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  data-testid="source-link"
                >
                  <FileText className="h-3 w-3" />
                  {rfi.source_reference || rfi.source_type}
                  <ArrowRight className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>

          {/* Action buttons - responsive grid */}
          <div className="flex gap-1.5 sm:gap-2 flex-wrap">
            {canEdit && (
              <Button
                onClick={() => setEditDialogOpen(true)}
                variant="outline"
                size="sm"
                className="rounded-sm text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
                data-testid="edit-rfi-button"
              >
                <Edit3 className="h-3.5 w-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline">Edit</span>
              </Button>
            )}
            <SendToTaskGenerator
              projectId={projectId}
              moduleType="RFI"
              moduleEntityId={rfiId}
              moduleReference={rfi.rfi_number || `Draft-${rfiId.slice(0, 8)}`}
            />
            <DocumentGeneratorButton
              documentType="RFI"
              projectId={projectId}
              entityId={rfiId}
              entityData={rfi}
              projectData={project}
            />
            {canIssue && (
              <Button
                onClick={openIssueDialog}
                disabled={issuing}
                size="sm"
                className="bg-blue-600 text-white hover:bg-blue-700 text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
                data-testid="issue-rfi-button"
              >
                {issuing ? (
                  <Loader2 className="h-3.5 w-3.5 sm:mr-1.5 animate-spin" />
                ) : (
                  <Send className="h-3.5 w-3.5 sm:mr-1.5" />
                )}
                <span className="hidden sm:inline">{rfiStatus === 'draft' || rfiStatus === 'open' ? 'Issue RFI' : 'Re-issue'}</span>
                <span className="sm:hidden">{rfiStatus === 'draft' || rfiStatus === 'open' ? 'Issue' : 'Re-issue'}</span>
              </Button>
            )}
            {(rfiStatus === 'issued' || rfiStatus === 'awaiting response' || rfiStatus === 'overdue') && (
              <Button
                variant="outline"
                onClick={handleSendReminder}
                disabled={sendingReminder}
                size="sm"
                className="border-amber-500 text-amber-600 hover:bg-amber-50 text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
                data-testid="send-rfi-reminder-btn"
              >
                {sendingReminder ? (
                  <Loader2 className="h-3.5 w-3.5 sm:mr-1.5 animate-spin" />
                ) : (
                  <Bell className="h-3.5 w-3.5 sm:mr-1.5" />
                )}
                <span className="hidden sm:inline">Reminder</span>
              </Button>
            )}
            {canRespond && (
              <Button
                onClick={() => setResponseDialogOpen(true)}
                variant="outline"
                size="sm"
                className="border-green-500 text-green-600 hover:bg-green-50 text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
              >
                <MessageSquare className="h-3.5 w-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline">Response</span>
              </Button>
            )}
            {canClose && (
              <Button
                onClick={() => setCloseDialogOpen(true)}
                variant="outline"
                size="sm"
                className="border-slate-500 text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3"
              >
                <CheckCircle2 className="h-3.5 w-3.5 sm:mr-1.5" />
                <span className="hidden sm:inline">Close</span>
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
          {/* Main Content */}
          <div className="xl:col-span-2 space-y-4 sm:space-y-6">
            {/* Query */}
            <div className="bg-white border border-slate-200 rounded-sm p-6">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Query</h3>
              <div className="prose prose-sm max-w-none">
                {rfi.query_formatted ? (
                  <div>
                    <Badge className="bg-green-100 text-green-700 mb-2">AI Formatted</Badge>
                    <p className="whitespace-pre-wrap">{rfi.query_formatted}</p>
                    <details className="mt-4">
                      <summary className="text-xs text-slate-500 cursor-pointer">View Original</summary>
                      <p className="whitespace-pre-wrap text-slate-600 mt-2">{rfi.query}</p>
                    </details>
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap">{rfi.query}</p>
                )}
              </div>
            </div>

            {/* Communication Log */}
            <div className="bg-white border border-slate-200 rounded-sm p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Communication Log ({rfi.communication_log?.length || 0})
                </h3>
                {rfi.communication_log?.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAnalyzeCorrespondence}
                    disabled={analyzingCorrespondence}
                    data-testid="ai-analyze-correspondence-btn"
                    className="text-xs"
                  >
                    {analyzingCorrespondence ? (
                      <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Analyzing...</>
                    ) : (
                      <><Brain className="h-3.5 w-3.5 mr-1.5" /> AI Analyze</>
                    )}
                  </Button>
                )}
              </div>

              {/* AI Analysis Result */}
              {aiAnalysis && (
                <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded-sm p-4" data-testid="ai-analysis-result">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Brain className="h-4 w-4 text-indigo-600" />
                      <span className="text-sm font-semibold text-indigo-700">AI Correspondence Analysis</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-indigo-500">{aiAnalysis.summary?.total_messages} messages analyzed</span>
                      <Button variant="ghost" size="sm" onClick={() => setAiAnalysis(null)} className="h-6 w-6 p-0">
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap text-sm leading-relaxed">
                    {aiAnalysis.analysis}
                  </div>
                </div>
              )}

              {rfi.communication_log?.length > 0 ? (
                <div className="space-y-4">
                  {rfi.communication_log.map((entry) => (
                    <div 
                      key={entry.id} 
                      className={`rounded-sm border overflow-hidden ${
                        entry.is_consultant_response 
                          ? 'bg-green-50 border-green-200' 
                          : entry.type === 'email_reply'
                          ? 'bg-blue-50 border-blue-200'
                          : 'bg-slate-50 border-slate-200'
                      }`}
                    >
                      {/* Clickable Header */}
                      <div 
                        className="p-4 cursor-pointer hover:bg-opacity-70 transition-colors"
                        onClick={() => setExpandedLogEntry(expandedLogEntry === entry.id ? null : entry.id)}
                        data-testid={`comm-log-entry-${entry.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {entry.is_consultant_response ? (
                              <Badge className="bg-green-100 text-green-700">Response</Badge>
                            ) : entry.type === 'email_reply' ? (
                              <Badge className="bg-blue-100 text-blue-700">
                                <Mail className="h-3 w-3 mr-1" />Email Reply
                              </Badge>
                            ) : (
                              <Badge className="bg-slate-100 text-slate-700">{entry.type}</Badge>
                            )}
                            <span className="text-sm font-medium">{entry.from_name}</span>
                            {entry.from_email && entry.type === 'email_reply' && (
                              <span className="text-xs text-slate-400">&lt;{entry.from_email}&gt;</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">{formatDateTime(entry.date || entry.created_at)}</span>
                            {expandedLogEntry === entry.id ? (
                              <ChevronUp className="h-4 w-4 text-slate-400" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-slate-400" />
                            )}
                          </div>
                        </div>
                        {entry.subject && (
                          <p className="text-sm font-medium text-slate-700 mb-1">{entry.subject}</p>
                        )}
                        {/* Truncated body preview when collapsed */}
                        {expandedLogEntry !== entry.id && (entry.body || entry.message) && (
                          <p className="text-sm text-slate-600 line-clamp-2">{entry.body || entry.message}</p>
                        )}
                        {/* Attachments indicator */}
                        {entry.attachments?.length > 0 && expandedLogEntry !== entry.id && (
                          <div className="flex items-center gap-2 mt-2 text-blue-600">
                            <Paperclip className="h-3 w-3" />
                            <span className="text-xs">{entry.attachments.length} attachment(s)</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Expanded Details Section */}
                      {expandedLogEntry === entry.id && (
                        <div className="border-t border-slate-200 bg-white">
                          {/* Full Body */}
                          {(entry.body || entry.message) && (
                            <div className="p-4 border-b border-slate-100">
                              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Message</p>
                              <p className="text-sm text-slate-700 whitespace-pre-wrap">{entry.body || entry.message}</p>
                            </div>
                          )}
                          
                          {/* Email Details */}
                          {(entry.from_email || entry.to_emails?.length > 0 || entry.cc_emails?.length > 0) && (
                            <div className="p-4 border-b border-slate-100 bg-slate-50">
                              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Email Info</p>
                              <div className="space-y-1 text-sm">
                                {entry.from_email && (
                                  <p><span className="text-slate-500">From:</span> {entry.from_name} &lt;{entry.from_email}&gt;</p>
                                )}
                                {entry.to_emails?.length > 0 && (
                                  <p><span className="text-slate-500">To:</span> {entry.to_emails.join(', ')}</p>
                                )}
                                {entry.cc_emails?.length > 0 && (
                                  <p><span className="text-slate-500">CC:</span> {entry.cc_emails.join(', ')}</p>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Download Original Email */}
                          {entry.correspondence_id && (
                            <div className="p-4 border-b border-slate-100 flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadEml(entry.correspondence_id);
                                }}
                                data-testid={`download-eml-${entry.id}`}
                                className="text-xs"
                              >
                                <Download className="h-3.5 w-3.5 mr-1.5" />
                                Download Original Email (.eml)
                              </Button>
                            </div>
                          )}
                          
                          {/* Attachments */}
                          {entry.attachments?.length > 0 && (
                            <div className="p-4">
                              <p className="text-xs font-semibold text-slate-500 uppercase mb-3">
                                Attachments ({entry.attachments.length})
                              </p>
                              <div className="space-y-2">
                                {entry.attachments.map((att, attIdx) => (
                                  <div 
                                    key={attIdx}
                                    className="flex items-center justify-between p-3 bg-slate-50 rounded border border-slate-200 hover:border-blue-300 transition-colors"
                                  >
                                    <div className="flex items-center gap-3">
                                      {(att.filename || att.file_name || '').match(/\.(pdf)$/i) ? (
                                        <FileText className="h-5 w-5 text-red-500" />
                                      ) : (att.filename || att.file_name || '').match(/\.(jpg|jpeg|png|gif)$/i) ? (
                                        <FileText className="h-5 w-5 text-green-500" />
                                      ) : (att.filename || att.file_name || '').match(/\.(msg|eml)$/i) ? (
                                        <Mail className="h-5 w-5 text-blue-500" />
                                      ) : (
                                        <Paperclip className="h-5 w-5 text-slate-500" />
                                      )}
                                      <div>
                                        <p className="text-sm font-medium text-slate-700">{att.filename || att.file_name || 'Attachment'}</p>
                                        {(att.size || att.file_size) && (
                                          <p className="text-xs text-slate-400">
                                            {((att.size || att.file_size) / 1024).toFixed(1)} KB
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    {entry.correspondence_id && att.id ? (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDownloadAttachment(entry.correspondence_id, att.id, att.filename || att.file_name);
                                        }}
                                        className="text-xs"
                                      >
                                        <Download className="h-3 w-3 mr-1" />
                                        Download
                                      </Button>
                                    ) : (att.file_url || att.url) ? (
                                      <a
                                        href={att.file_url || att.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <Download className="h-3 w-3" />
                                        Download
                                      </a>
                                    ) : null}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* No details message */}
                          {(!entry.attachments || entry.attachments.length === 0) && !entry.body && !entry.message && (
                            <div className="p-4 text-center text-slate-400 text-sm">
                              No additional details available
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No communication logged yet</p>
              )}
            </div>

            {/* Narrative */}
            <div className="bg-white border border-slate-200 rounded-sm p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  AI Narrative
                </h3>
                <Button
                  onClick={handleGenerateNarrative}
                  disabled={generatingNarrative}
                  variant="outline"
                  size="sm"
                >
                  {generatingNarrative ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Wand2 className="h-4 w-4 mr-2" />
                  )}
                  Generate Narrative
                </Button>
              </div>
              {rfi.narrative ? (
                <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-4">
                  <p className="text-sm text-indigo-900 whitespace-pre-wrap">{rfi.narrative}</p>
                  {rfi.narrative_generated_at && (
                    <p className="text-xs text-indigo-600 mt-2">
                      Generated: {formatDateTime(rfi.narrative_generated_at)}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No narrative generated yet</p>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 sm:space-y-6">
            {/* Info Panel */}
            <div className="bg-white border border-slate-200 rounded-sm p-4 sm:p-6">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Information</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-slate-500">Discipline</p>
                  <p className="text-sm font-medium">{rfi.discipline || <span className="text-amber-500 italic">Not set</span>}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Created</p>
                  <p className="text-sm font-medium">{formatDate(rfi.date_created)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Date Issued</p>
                  <p className="text-sm font-medium">{formatDate(rfi.date_issued)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Response Due</p>
                  <p className={`text-sm font-medium ${!rfi.required_response_date && rfiStatus !== 'closed' ? 'text-amber-500 italic' : ''}`}>
                    {rfi.required_response_date ? formatDate(rfi.required_response_date) : (rfiStatus !== 'closed' ? 'Not set — edit to add' : 'N/A')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Date Answered</p>
                  <p className="text-sm font-medium">{formatDate(rfi.date_answered)}</p>
                </div>
                {rfi.days_outstanding !== null && rfi.days_outstanding !== undefined && (
                  <div>
                    <p className="text-xs text-slate-500">Days Outstanding</p>
                    <p className={`text-sm font-medium ${
                      rfi.aging_category === 'Overdue' ? 'text-red-600' :
                      rfi.aging_category === 'Red' ? 'text-red-500' :
                      rfi.aging_category === 'Amber' ? 'text-amber-500' : 'text-green-500'
                    }`}>
                      {rfi.days_outstanding} days
                    </p>
                  </div>
                )}
                {rfi.response_days !== null && rfi.response_days !== undefined && (
                  <div>
                    <p className="text-xs text-slate-500">Response Time</p>
                    <p className="text-sm font-medium">{rfi.response_days} days</p>
                  </div>
                )}
                {rfi.linked_activity && (
                  <div>
                    <p className="text-xs text-slate-500">Linked Activity</p>
                    <p className="text-sm font-medium">{rfi.linked_activity}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-slate-500">Critical Path</p>
                  <p className={`text-sm font-medium ${rfi.affects_critical_path ? 'text-red-600' : 'text-slate-400'}`}>
                    {rfi.affects_critical_path ? 'Yes — Affects Critical Path' : 'No'}
                  </p>
                </div>
                {rfi.closure_effect && (
                  <div>
                    <p className="text-xs text-slate-500">Closure Effect</p>
                    <Badge className={
                      rfi.closure_effect === 'No Impact' ? 'bg-green-100 text-green-700' :
                      rfi.closure_effect === 'Potential Delay' ? 'bg-amber-100 text-amber-700' :
                      rfi.closure_effect === 'Confirmed Delay' ? 'bg-red-100 text-red-700' :
                      'bg-purple-100 text-purple-700'
                    }>
                      {rfi.closure_effect}
                    </Badge>
                  </div>
                )}
                {rfi.related_delay_event_id && (
                  <div>
                    <p className="text-xs text-slate-500">Linked Delay Event</p>
                    <Button
                      variant="link"
                      className="p-0 h-auto text-sm text-blue-600"
                      onClick={() => navigate(`/projects/${projectId}/events/${rfi.related_delay_event_id}`)}
                    >
                      View Delay Event <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Recipients */}
            <div className="bg-white border border-slate-200 rounded-sm p-4 sm:p-6">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">
                Recipients ({rfi.recipients?.length || 0})
              </h3>
              {rfi.recipients?.length > 0 ? (
                <div className="space-y-2">
                  {rfi.recipients.map((r) => (
                    <div key={r.id} className="flex items-center gap-2 text-sm">
                      {r.is_internal ? (
                        <User className="h-4 w-4 text-slate-400" />
                      ) : (
                        <Building2 className="h-4 w-4 text-slate-400" />
                      )}
                      <span>{r.name}</span>
                      {r.is_cc && <Badge variant="secondary" className="text-xs">CC</Badge>}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No recipients added</p>
              )}
            </div>

            {/* Attachments */}
            <div className="bg-white border border-slate-200 rounded-sm p-4 sm:p-6">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">
                Attachments ({rfi.attachments?.length || 0})
              </h3>
              {rfi.attachments?.length > 0 ? (
                <div className="space-y-2">
                  {rfi.attachments.map((att) => (
                    <a
                      key={att.id}
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/rfi/attachment/${rfiId}/${att.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-2 bg-slate-50 rounded-sm hover:bg-slate-100"
                    >
                      <FileText className="h-4 w-4 text-slate-500" />
                      <span className="text-sm text-slate-700 flex-1 truncate">{att.file_name}</span>
                      <Download className="h-4 w-4 text-slate-400" />
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No attachments</p>
              )}
            </div>

            {/* Timeline */}
            <div className="bg-white border border-slate-200 rounded-sm p-4 sm:p-6">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">
                Timeline
              </h3>
              {timeline.length > 0 ? (
                <div className="space-y-3">
                  {timeline.map((entry, idx) => (
                    <div key={idx} className="flex gap-3">
                      <div className={`w-2 h-2 rounded-full mt-1.5 ${
                        entry.type === 'response' ? 'bg-green-500' :
                        entry.type === 'rfi_closed' ? 'bg-slate-500' :
                        entry.type === 'delay_event_created' ? 'bg-red-500' :
                        'bg-blue-500'
                      }`} />
                      <div>
                        <p className="text-sm font-medium">{entry.title}</p>
                        <p className="text-xs text-slate-500">{formatDateTime(entry.date)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No timeline events</p>
              )}
            </div>
          </div>
        </div>

        {/* Email Correspondence Section */}
        <div className="mt-6 sm:mt-8 bg-white border border-slate-200 rounded-sm p-4 sm:p-6" data-testid="rfi-correspondence-section">
          <CorrespondenceTab 
            projectId={projectId}
            moduleType="RFI"
            moduleEntityId={rfiId}
            title="Email Correspondence"
          />
        </div>

        {/* Admin Delete Section */}
        {isAdmin && (
          <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-red-200 max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-wider text-red-600">Danger Zone</h3>
                <p className="text-sm text-slate-500 mt-1">Permanently delete this RFI and all associated data.</p>
              </div>
              {permissions.can('rfis', 'delete') && (
              <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="border-red-500 text-red-600 hover:bg-red-50"
                    data-testid="delete-rfi-button"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete RFI
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="text-xl font-barlow font-bold uppercase text-red-600">Delete RFI</DialogTitle>
                    <DialogDescription>
                      Are you sure you want to delete this RFI? This action cannot be undone.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-sm p-4">
                      <p className="text-sm text-red-700">
                        <strong>Warning:</strong> This will permanently delete the RFI, all communications, and attachments.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setDeleteDialogOpen(false)}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleDelete}
                        disabled={deleting}
                        className="flex-1 bg-red-600 text-white hover:bg-red-700"
                        data-testid="confirm-delete-rfi-button"
                      >
                        {deleting ? 'Deleting...' : 'Delete Permanently'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              )}
            </div>
          </div>
        )}

        {/* Record Response Dialog */}
        <Dialog open={responseDialogOpen} onOpenChange={setResponseDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                {isProfessionalRole(user?.role) 
                  ? 'Submit Your Response' 
                  : 'Record Consultant Response'}
              </DialogTitle>
              <DialogDescription>
                {isProfessionalRole(user?.role)
                  ? 'You are responding as the authenticated consultant. Your details will be recorded with this response.'
                  : 'Enter the response details received from the consultant. You can upload the original email (.msg) or attachments.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              {/* Show authenticated user info for consultants */}
              {isProfessionalRole(user?.role) ? (
                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-800">Authenticated Respondent</span>
                  </div>
                  <div className="text-sm text-green-700">
                    <p><strong>Name:</strong> {user.name || user.full_name}</p>
                    <p><strong>Email:</strong> {user.email}</p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Respondent Name *</Label>
                    <select
                      value={responseForm.respondent_name}
                      onChange={(e) => {
                        const selectedName = e.target.value;
                        // Find the recipient and auto-populate email
                        const recipient = rfi?.recipients?.find(r => r.name === selectedName);
                        setResponseForm(prev => ({ 
                          ...prev, 
                          respondent_name: selectedName,
                          respondent_email: recipient?.email || prev.respondent_email
                        }));
                      }}
                      className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                    >
                      <option value="">Select respondent</option>
                      {rfi?.recipients?.map((recipient, idx) => (
                        <option key={idx} value={recipient.name}>
                          {recipient.name} {recipient.company ? `(${recipient.company})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Respondent Email *</Label>
                    <Input
                      value={responseForm.respondent_email}
                      onChange={(e) => setResponseForm(prev => ({ ...prev, respondent_email: e.target.value }))}
                      placeholder="john@consultant.com"
                      type="email"
                      readOnly
                      className="bg-slate-50"
                    />
                  </div>
                </div>
              )}
              <div>
                <Label>Response *</Label>
                <Textarea
                  value={responseForm.response_text}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, response_text: e.target.value }))}
                  placeholder="Enter the consultant's response or paste the email content..."
                  rows={6}
                />
              </div>
              
              {/* File Upload Section with Drag & Drop */}
              <div 
                className={`border-2 border-dashed rounded-lg p-4 transition-colors ${
                  isDragging 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-slate-300 bg-slate-50'
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragging(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragging(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsDragging(false);
                  const files = Array.from(e.dataTransfer.files);
                  handleDroppedFiles(files);
                }}
              >
                <Label className="text-sm font-medium mb-2 block">
                  <Upload className="h-4 w-4 inline mr-1" />
                  Upload Email or Attachments
                </Label>
                <p className="text-xs text-slate-500 mb-3">
                  <strong>Drag & drop</strong> Outlook emails (.msg) directly here, or click to browse.
                  <br />
                  Supported: .msg, .eml (Outlook emails), .pdf, .doc, .docx, images
                </p>
                
                <div className="text-center py-4">
                  {isDragging ? (
                    <div className="text-blue-600">
                      <Upload className="h-8 w-8 mx-auto mb-2 animate-bounce" />
                      <p className="font-medium">Drop files here</p>
                    </div>
                  ) : (
                    <>
                      <input
                        type="file"
                        id="response-files"
                        multiple
                        accept=".msg,.eml,.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                        onChange={handleResponseFileChange}
                        className="hidden"
                      />
                      <label 
                        htmlFor="response-files"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 rounded-md cursor-pointer hover:bg-slate-100 transition-colors"
                      >
                        <Upload className="h-4 w-4" />
                        Choose Files
                      </label>
                      <p className="text-xs text-slate-400 mt-2">or drag & drop files here</p>
                    </>
                  )}
                </div>
                
                {/* Selected Files */}
                {responseAttachments.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {responseAttachments.map((file, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between p-2 bg-white rounded border"
                      >
                        <div className="flex items-center gap-2">
                          {file.name.endsWith('.msg') || file.name.endsWith('.eml') ? (
                            <Mail className="h-4 w-4 text-blue-500" />
                          ) : file.name.endsWith('.pdf') ? (
                            <FileText className="h-4 w-4 text-red-500" />
                          ) : (
                            <Paperclip className="h-4 w-4 text-slate-500" />
                          )}
                          <span className="text-sm truncate max-w-[250px]">{file.name}</span>
                          <span className="text-xs text-slate-400">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeResponseAttachment(index)}
                          className="h-6 w-6 p-0 text-slate-400 hover:text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => {
                  setResponseDialogOpen(false);
                  setResponseAttachments([]);
                }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleRecordResponse} 
                  disabled={uploadingResponse}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {uploadingResponse ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Record Response
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Close RFI Dialog */}
        <Dialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Close RFI</DialogTitle>
              <DialogDescription>
                Select the closure effect and optionally convert to a delay event.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Closure Effect *</Label>
                <Select 
                  value={closureForm.closure_effect} 
                  onValueChange={(v) => setClosureForm(prev => ({ ...prev, closure_effect: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select effect" />
                  </SelectTrigger>
                  <SelectContent>
                    {closureEffects.map(effect => (
                      <SelectItem key={effect} value={effect}>{effect}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Closure Notes / Description</Label>
                <Textarea
                  value={closureForm.closure_notes}
                  onChange={(e) => setClosureForm(prev => ({ ...prev, closure_notes: e.target.value }))}
                  placeholder="Describe how the RFI was resolved..."
                  rows={3}
                />
              </div>
              
              {/* Closed By and Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Closed By *</Label>
                  <Input
                    value={closureForm.closed_by}
                    onChange={(e) => setClosureForm(prev => ({ ...prev, closed_by: e.target.value }))}
                    placeholder="Name of person closing"
                  />
                </div>
                <div>
                  <Label>Closure Date *</Label>
                  <Input
                    type="date"
                    value={closureForm.closure_date}
                    onChange={(e) => setClosureForm(prev => ({ ...prev, closure_date: e.target.value }))}
                  />
                </div>
              </div>
              
              {/* Closure Attachments */}
              <div>
                <Label className="mb-2 block">Attachments (Email / Files)</Label>
                <input
                  type="file"
                  ref={closureFileInputRef}
                  onChange={(e) => handleClosureAttachmentUpload(e.target.files)}
                  className="hidden"
                  multiple
                  accept=".msg,.eml,.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => closureFileInputRef.current?.click()}
                  disabled={closureUploading}
                  className="mb-2"
                >
                  {closureUploading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Upload Files
                </Button>
                {closureForm.closure_attachments.length > 0 && (
                  <div className="space-y-1 mt-2">
                    {closureForm.closure_attachments.map((att, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-slate-50 px-2 py-1 rounded text-sm">
                        <span className="truncate">{att.filename}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeClosureAttachment(idx)}
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {(closureForm.closure_effect === 'Confirmed Delay' || closureForm.closure_effect === 'Potential Delay') && (
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="convert-delay"
                    checked={closureForm.convert_to_delay_event}
                    onChange={(e) => setClosureForm(prev => ({ ...prev, convert_to_delay_event: e.target.checked }))}
                    className="h-4 w-4"
                  />
                  <label htmlFor="convert-delay" className="text-sm font-medium cursor-pointer">
                    Convert to Delay Event
                  </label>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setCloseDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleClose} className="bg-slate-900 hover:bg-slate-800">
                  Close RFI
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit RFI Dialog */}
        <RFIForm
          projectId={projectId}
          rfiId={rfiId}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          onSuccess={() => {
            setEditDialogOpen(false);
            loadRFI();
            toast.success('RFI updated successfully');
          }}
        />

        {/* Issue RFI Dialog */}
        <Dialog open={issueDialogOpen} onOpenChange={setIssueDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Issue RFI
              </DialogTitle>
              <DialogDescription>
                Send this RFI to the selected recipients
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm font-medium">Send Type</Label>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  <Button
                    type="button"
                    variant={issueForm.send_type === 'external' ? 'default' : 'outline'}
                    className={issueForm.send_type === 'external' ? 'bg-blue-600' : ''}
                    onClick={() => setIssueForm({ ...issueForm, send_type: 'external' })}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    External
                  </Button>
                  <Button
                    type="button"
                    variant={issueForm.send_type === 'internal' ? 'default' : 'outline'}
                    className={issueForm.send_type === 'internal' ? 'bg-blue-600' : ''}
                    onClick={() => setIssueForm({ ...issueForm, send_type: 'internal' })}
                  >
                    <Building2 className="h-4 w-4 mr-2" />
                    Internal
                  </Button>
                  <Button
                    type="button"
                    variant={issueForm.send_type === 'both' ? 'default' : 'outline'}
                    className={issueForm.send_type === 'both' ? 'bg-green-600' : ''}
                    onClick={() => setIssueForm({ ...issueForm, send_type: 'both' })}
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Both
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {issueForm.send_type === 'external' 
                    ? 'Send to external consultants/contractors' 
                    : issueForm.send_type === 'internal'
                    ? 'Send to internal team members'
                    : 'Send to both internal team and external consultants'}
                </p>
              </div>

              <div className="p-3 bg-slate-50 rounded-lg">
                <Label className="text-sm font-medium mb-2 block">Recipients</Label>
                {rfi.recipients?.length > 0 ? (
                  <div className="space-y-1">
                    {rfi.recipients.map((r, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span>{r.name}</span>
                        <span className="text-slate-500">{r.email}</span>
                        {r.is_cc && <Badge variant="outline" className="text-xs ml-2">CC</Badge>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No recipients added</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="send_email"
                  checked={issueForm.send_email}
                  onChange={(e) => setIssueForm({ ...issueForm, send_email: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="send_email" className="text-sm">
                  Send email notification with PDF attachment
                </Label>
              </div>

              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-700">
                  <strong>What happens when you issue:</strong>
                </p>
                <ul className="text-xs text-blue-600 mt-1 space-y-1">
                  <li>• Status changes to "Awaiting Response"</li>
                  <li>• Email sent to recipients with PDF attached</li>
                  <li>• Activity logged in consultant portal</li>
                  <li>• Communication recorded in RFI history</li>
                </ul>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIssueDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleIssue} 
                disabled={issuing}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {issuing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Issuing...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Issue RFI
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
